import healthyHabitsRepository from '@/repositories/HealthyHabitsRepository'
import { DAILY_GOAL, WEEKLY_GOAL, MONTHLY_GOAL } from '@/constants/healthyHabits'
import { GRAY_400_COLOUR, BACKGROUND_COLOUR } from '@/constants/colours'
import { getBaseDomain } from '@/helpers/global.helpers'
import { getMonthName, getMonthShortName, getWeekDayShortName, getWeekDaysShortNames } from '@/helpers/date.helpers'
import { formatDateForGraphAxis } from '../helpers/date.helpers'

const DEFAULT_PROMISE = new Promise(() => {})

export default {
	computed: {
		historyPatientId () {
			return this.$route.params.historyPatientId || sessionStorage.getItem('healthyHabitsPatientId') || this.$store.state.healthyHabits.historyPatientId
		}
	},
	methods: {
		// Repository wrapper methods
		logOffNotAuthorized () {
			window.$cookies.remove('api_token', null, getBaseDomain())
			this.$store.state.session.loginCallback = function () {
					this.$router.push('/').catch(() => {})
			}
			this.$router.push('/inicio-sesion')
		},
		sessionCookieIsValid (serverData) {
			if ((serverData.response && serverData.response.status === 401) || (serverData.data && serverData.data.errorId)) {
					this.$store.dispatch('showInfoAlert', serverData.data && serverData.data.errorId === '403' ? this.$t('professionalNotAllowed') : this.$t('sessionExpired'))
					setTimeout(() => {
							this.logOffNotAuthorized()
					}, 4000);
					return false
			}
			return true
		},
		async processPromise (promise) {
			if (this.sessionCookieIsValid(await promise)) {
				return promise
			} else {
				return DEFAULT_PROMISE
			}
		},
		getTableData (allData) {
			let limitDate = this.$store.state.healthyHabits.table.limitDate
			let date = `${limitDate.getFullYear()}-${('0' + (limitDate.getMonth() + 1)).slice(-2)}-${('0' + limitDate.getDate()).slice(-2)}T00:00:00`
			const promise = healthyHabitsRepository.getTableData(date, this.$store.state.healthyHabits.table.prevDates, allData, this.historyPatientId)
			return this.processPromise(promise)
		},
		updateTableData (data) {
			return this.processPromise(healthyHabitsRepository.updateTableData(data))
    },
    removeTableData (data) {
			return this.processPromise(healthyHabitsRepository.removeTableData(data))
    },
		getStatistics () {
			let promise = healthyHabitsRepository.getStatistics(this.$store.state.healthyHabits.table.itemSelectedId, this.historyPatientId)
			return this.processPromise(promise)
		},
		getUserSetting () {
			this.processPromise(healthyHabitsRepository.getUserSetting()).then((serverData) => {
				if (serverData.data) {
					this.processHealthyHabitsSetting(serverData.data)
				}
			})
		},
		updateFormData (data) {
			return this.processPromise(healthyHabitsRepository.updateFormData(data))
		},
		createDataForm (data) {
			return this.processPromise(healthyHabitsRepository.createDataForm(data))
		},
		getHealthyHabitsStatistics (id) {
			this.$store.state.healthyHabits.table.itemSelectedId = id
			this.getStatistics().then((serverData) => {
				this.processHealthyHabitsDataForStatistics(serverData.data)
			})
		},
		getHealthyHabitsData (allData, partialData) {
			this.getMaxMinDate(!partialData)
			this.$store.state.healthyHabits.table.limitDate = partialData ? this.$store.state.healthyHabits.table.limitDate : this.getCurrentDateUtc()
			this.getTableData(allData).then((serverData) => {
				this.$store.state.healthyHabits.hideLoadingIcon = false
				this.$store.state.healthyHabits.table.minDateServer = new Date(serverData.data.minDate)
				this.$store.state.healthyHabits.table.maxDateServer = new Date(serverData.data.maxDate)
				if (partialData) {
					this.processHealthyHabitsPartialData(serverData.data)
				} else {
					this.$store.state.healthyHabits.table.data = this.processHealthyHabitsDataForTable(serverData.data)
				}
			})
		},
		processHealthyHabitsSetting (settingData) {
			if (settingData.isProfessional) {
				if (this.$route.params.historyPatientId) sessionStorage.setItem("healthyHabitsPatientId", this.$route.params.historyPatientId)
				this.$store.state.healthyHabits.setting.isProfessional = true
				this.$store.state.healthyHabits.isReadOnlyView = true
				return
			}
			this.$store.state.healthyHabits.setting.nick = settingData.nick
			this.$store.state.healthyHabits.setting.habitUserId = settingData.habitUserId
			this.$store.state.healthyHabits.setting.notifications.email = settingData.notificationsEmail
			this.$store.state.healthyHabits.setting.notifications.phone = settingData.notificationsPhone
			this.$store.state.healthyHabits.setting.showStoredHabits = settingData.showStoredHabits
			this.$store.state.healthyHabits.setting.originalShowStoredHabits = settingData.showStoredHabits
			this.$store.state.healthyHabits.setting.originalShowGoalCompletedHabits = settingData.showGoalCompletedHabits
			this.$store.state.healthyHabits.setting.showGoalCompletedHabits = settingData.showGoalCompletedHabits
			this.$store.state.healthyHabits.setting.allowFreeDays = settingData.allowFreeDays
			this.$store.state.healthyHabits.setting.originalAllowFreeDays = settingData.allowFreeDays
			this.$store.state.healthyHabits.setting.invertedCalendarDays = settingData.invertedCalendarDays
		},
		processHealthyHabitsDataForStatistics(chartData) {
			if (chartData) {
				this.$store.state.healthyHabits.chart.data = []
				this.$store.state.healthyHabits.chart.title = chartData.description
				this.$store.state.healthyHabits.chart.question = chartData.question
				this.$store.state.healthyHabits.chart.notes = chartData.notes
				this.$store.state.healthyHabits.chart.reminder = chartData.reminder
				this.$store.state.healthyHabits.chart.reminderDays = chartData.reminderDays
				this.$store.state.healthyHabits.chart.goalFrequencyId = chartData.goalFrequencyId
				this.$store.state.healthyHabits.chart.goal = chartData.goal
				this.$store.state.healthyHabits.chart.goalCompleted = chartData.goalCompleted
				this.$store.state.healthyHabits.chart.measurable = chartData.measurable
				this.$store.state.healthyHabits.chart.measureUnit = chartData.measureUnit
				this.$store.state.healthyHabits.chart.color = chartData.color
				this.$store.state.healthyHabits.chart.total = chartData.totalDays
				this.$store.state.healthyHabits.chart.bestStreak = chartData.bestStreakDays
				this.$store.state.healthyHabits.chart.currentStreak = chartData.currentStreakDays
				for (let item in chartData.data) {
					let currentItem = chartData.data[item]
					this.$store.state.healthyHabits.chart.data.push({ date: new Date(currentItem.date), value: parseFloat(currentItem.value) >= 0 ? parseFloat(currentItem.value) : currentItem.value === 'true' ? 1 : currentItem.value === 'false' ? 0 : null })
				}
				this.$store.state.healthyHabits.chart.dataMapped = this.processHealthyHabitsDataMapped(Object.assign({}, this.$store.state.healthyHabits.chart))
			} else {
				let newValue = this.$store.state.healthyHabits.itemSelected.newValue
				let newValueFormatted = parseFloat(newValue) >= 0 ? parseFloat(newValue) : newValue === true ? 1 : newValue === false ? 0 : null
				if (parseFloat(newValue) === 0 || newValue === false) {
					this.$store.state.healthyHabits.chart.data = this.$store.state.healthyHabits.chart.data.filter(data => this.getDateOnlyFormatted(data.date) !== this.$store.state.healthyHabits.itemSelected.date)
					delete this.$store.state.healthyHabits.chart.dataMapped[this.$store.state.healthyHabits.itemSelected.date]
				} else {
					let dataExists = this.$store.state.healthyHabits.chart.data.some(data => this.getDateOnlyFormatted(data.date) === this.$store.state.healthyHabits.itemSelected.date)
					if (dataExists) {
						this.$store.state.healthyHabits.chart.data.find(data => this.getDateOnlyFormatted(data.date) === this.$store.state.healthyHabits.itemSelected.date).value = newValueFormatted
					} else {
						this.$store.state.healthyHabits.chart.data.push({ date: new Date(this.$store.state.healthyHabits.itemSelected.date), value: newValueFormatted })
					}
					this.$store.state.healthyHabits.chart.dataMapped[this.$store.state.healthyHabits.itemSelected.date] = parseFloat(newValue) >= 0 ? parseFloat(newValue) : newValue
				}
			}
			this.$store.state.healthyHabits.chart.data.sort((a, b) => a.date - b.date)
			if (this.$store.state.healthyHabits.chart.data.length > 0 && this.$store.state.healthyHabits.chart.data.some(h => h.value !== null)) {
				this.getHistoryOptions(!chartData)
				this.getFrequencyOptions(!chartData)
				this.getScoreOptions(!chartData)
			} else {
				this.$store.state.healthyHabits.chart.history.options = {}
				this.$store.state.healthyHabits.chart.frequency.options = {}
				this.$store.state.healthyHabits.chart.score.options = {}
				this.$store.state.healthyHabits.chart.score.currentValue = 0
			}
			if (this.$store.state.healthyHabits.itemSelected.fromCalendar) {
				this.generateCalendar(true)
			}
			this.$store.state.healthyHabits.chart.showChart = true
		},
		processHealthyHabitsDataForTable (tableData) {
			let habitsIdToResetCurrentStreak = []
			let fields = [{ key: 'description', label: '' }, { key: 'nextDates', label: '' }, ...this.getHeaderDatesInIntervalFormatted(this.$store.state.healthyHabits.table.minDate, this.$store.state.healthyHabits.table.maxDate), { key: 'prevDates', label: '' }, { key: 'actions', label: '' }]
			// if (!this.mobileVersion()) fields = [...fields, { key: 'sliderSection1', label: '' }, { key: 'sliderSection2', label: '' }, { key: 'sliderSection3', label: '' }]
			let items = tableData.data.length === 0 ? [] : tableData.data.map(item => {
				let result = {}
				result.id = item.id
				result.description = item.description
				result.question = item.question
				result.reminder = item.reminder
				result.reminderDays = item.reminderDays
				result.notes = item.notes
				result.measurable = item.measurable
				result.measureUnit = item.measureUnit
				result.goal = item.goal || 1
				result.goalFrequencyId = item.goalFrequencyId
				result.bestStreak = item.bestStreakDays
				result.total = item.totalDays
				result.color = item.color
				result.createdAt = item.createdAt
				result.stored = item.stored
				result.isMultiUser = item.isMultiUser
				result.position = item.position
				result.goalCompleted = item.goalCompleted
				result.showActionButtons = false //only for client
				result.multiUserData = []
				result.multiUserInfo = []
				if (item.data.length > 0) {
					item.data.map(itemData => {
						if (result.measurable) {
							result[this.getDateOnlyFormatted(itemData.date)] = parseFloat(itemData.value)
						} else {
							result[this.getDateOnlyFormatted(itemData.date)] = itemData.value === 'true' ? true : itemData.value === 'false' || itemData.value === 0 ? false : null
						}
					})
				}
				if (item.multiUserInfo && item.multiUserInfo.length > 0) {
					let multiUserMapped = []
					if (item.multiUserData.length > 0) {
						for (let index in item.multiUserInfo) {
							let multiUserSingle = {}
							let healthyHabitsUserId = item.multiUserInfo[index].healthyHabitsUserId
							item.multiUserData.filter(x => x.healthyHabitsUserId === healthyHabitsUserId).map(itemData => {
								if (result.measurable) {
									multiUserSingle[this.getDateOnlyFormatted(itemData.date)] = parseFloat(itemData.value)
								} else {
									multiUserSingle[this.getDateOnlyFormatted(itemData.date)] = itemData.value === 'true' ? true : itemData.value === 'false' || itemData.value === 0 ? false : null
								}
							})
							multiUserSingle.goal = result.goal
							multiUserSingle.goalFrequencyId = result.goalFrequencyId
							multiUserSingle.healthyHabitsUserId = item.multiUserInfo[index].healthyHabitsUserId
							if (!result.measurable) {
								this.fillPartialGoalCompleted(multiUserSingle)
							}
							multiUserMapped.push(multiUserSingle)
						}
						result.multiUserData = multiUserMapped
					}
					result.multiUserInfo = item.multiUserInfo
				}
				result.accumulatedGoal = !this.$store.state.healthyHabits.table.data.items || !this.$store.state.healthyHabits.table.data.items.find(h => h.id === item.id) ? this.lastGoalAccumulated(result) : this.$store.state.healthyHabits.table.data.items.find(h => h.id === item.id).accumulatedGoal
				if (!result.measurable) {
					this.fillPartialGoalCompleted(result)
				}
				result.currentStreak = item.data.length > 0 ? this.getCurrentStreakOnStart(result.measurable ? this.fillMeasurablePartialGoalCompleted(result) : result, item.currentStreakDays) : item.currentStreakDays
				if (item.currentStreakDays !== result.currentStreak) {
					habitsIdToResetCurrentStreak.push(item.id)
				}
				return result
			})
			this.$store.state.healthyHabits.itemSelected.fromCalendar = false
			this.$store.state.healthyHabits.chart.showChart = false
			this.$store.state.healthyHabits.bestStreak = items.length > 0 ? Math.max(...items.map(item => item.bestStreak)) : 0
			items.sort((a, b) => a.position - b.position)
			if (habitsIdToResetCurrentStreak.length > 0) {
				this.$store.state.healthyHabits.hideLoadingIcon = true
				this.updateTableData({ habitsResetCurrentStreak: habitsIdToResetCurrentStreak }).then(data => {
					this.$store.state.healthyHabits.hideLoadingIcon = false
				})
			}
			return { fields, items }
		},
		processHealthyHabitsPartialData (tableData) {
			let currentData = this.$store.state.healthyHabits.table.data.items
			for (let index in tableData.data) {
				let item = tableData.data[index]
				let itemData = item.data
				let currentItem = currentData.find(h => h.id === item.id)
				let anyData = itemData && itemData.length > 0
				let minDate = anyData ? new Date(Math.min(...itemData.map(h => new Date(h.date)))) : null
				for (let y in itemData) {
					let data = itemData[y]
					currentItem[this.getDateOnlyFormatted(data.date)] = currentItem[this.getDateOnlyFormatted(data.date)] !== undefined ? currentItem[this.getDateOnlyFormatted(data.date)] : (item.measurable ? parseFloat(data.value) : data.value === 'true' ? true : data.value === 'false' || emptyData || data.value === 0 ? false : null)
				}
				if (!item.measurable && anyData) {
					this.fillPartialGoalCompleted(currentItem, minDate)
				}
				if (item.multiUserInfo.length > 0 && item.multiUserData.length > 0) {
					for (let index in item.multiUserInfo) {
						let healthyHabitsUserId = item.multiUserInfo[index].healthyHabitsUserId
						let multiUserDataExits = false
						let currentMultiUserData = []
						if (currentItem.multiUserData.find(h => h.healthyHabitsUserId === healthyHabitsUserId)) {
							currentMultiUserData = currentItem.multiUserData.find(h => h.healthyHabitsUserId === healthyHabitsUserId)
							multiUserDataExits = true
						}
						item.multiUserData.filter(x => x.healthyHabitsUserId === healthyHabitsUserId).map(itemData => {
							if (item.measurable) {
								currentMultiUserData[this.getDateOnlyFormatted(itemData.date)] = parseFloat(itemData.value)
							} else {
								currentMultiUserData[this.getDateOnlyFormatted(itemData.date)] = itemData.value === 'true' ? true : itemData.value === 'false' || itemData.value === 0 ? false : null
							}
						})
						if (!item.measurable) {
							this.fillPartialGoalCompleted(currentMultiUserData)
						}
						if (!multiUserDataExits) {
							currentMultiUserData.healthyHabitsUserId = healthyHabitsUserId
							currentMultiUserData.goal = currentItem.goal
							currentMultiUserData.goalFrequencyId = currentItem.goalFrequencyId
							currentItem.multiUserData = currentMultiUserData
						}
					}
					this.$store.state.healthyHabits.table.key++
				}
			}
		},
		computeNativeValue (item) {
			if (!item) {
				return undefined
			}
			return parseFloat(item.value) ? parseFloat(item.value) : item.value === 'true' ? true : item.value === 'false' || item.value === 0 ? false : null
		},
		processHealthyHabitsDataMapped (data) {
			let result = {}
			result.measurable = data.measurable
			result.measureUnit = data.measureUnit
			result.goal = data.goal || 1
			result.goalFrequencyId = data.goalFrequencyId
			result.goalCompleted = data.goalCompleted
			result.total = data.total
			result.bestStreak = data.bestStreak
			for (let item in data.data) {
				let currentItem = data.data[item]
				if (result.measurable) {
					result[this.getDateOnlyFormatted(currentItem.date)] = parseFloat(currentItem.value)
				} else {
					result[this.getDateOnlyFormatted(currentItem.date)] = currentItem.value === 1 ? true : currentItem.value === 0 ? false : null
				}
			}
			result.currentStreak = data.data.length > 0 ? this.getCurrentStreakOnStart(result.measurable ? this.fillMeasurablePartialGoalCompleted(result) : result, data.currentStreak) : data.currentStreak

			return result
		},
		getMaxMinDate (reset) {
			this.$store.state.healthyHabits.table.minDate = this.$store.state.healthyHabits.table.minDate && !reset ? this.$store.state.healthyHabits.table.minDate : new Date(new Date(new Date().setDate(new Date().getDate() - (this.tableNumberDaysVisible() - 1))).setHours(0, 0, 0, 0))
			this.$store.state.healthyHabits.table.maxDate = this.$store.state.healthyHabits.table.maxDate && !reset ? this.$store.state.healthyHabits.table.maxDate : this.getCurrentDateUtc()
		},
		getSummaryOptions (percentage) {
			this.$store.state.healthyHabits.chart.summary.options = {
				title: { text: this.$t('summary'), style: { color: this.$store.state.healthyHabits.chart.color } },
				chart: { backgroundColor: BACKGROUND_COLOUR, width: 200, height: 200 },
				tooltip: { enabled: false },
				title: {
					text: `${percentage}%`,
					verticalAlign: 'middle'
				},
				plotOptions: {
					pie: {
						size: '45%'
					}
				},
				credits: false,
				series: [{
					type: 'pie',
					enableMouseTracking: false,
					innerSize: '65%',
					dataLabels: [{ enabled: false }],
					data: [{y: percentage, color: this.$store.state.healthyHabits.chart.color}, percentage === 100 ? {} : {y: 100 - percentage, color: 'white'}],
				}]
			}
		},
		getHistoryOptions (refresh) {
			let _this = this
			let isEmpty = Object.keys(this.$store.state.healthyHabits.chart.history.options).length === 0
			if (refresh && !isEmpty) {
				this.historyIntervalSelected(this.$store.state.healthyHabits.chart.history.interval)
			} else {
				this.$store.state.healthyHabits.chart.history.options = {
					id: 'history-chart',
					title: { text: this.$t('historyTitle'), style: { color: this.$store.state.healthyHabits.chart.color } },
					xAxis: { type: 'datetime', title: { text: this.$t('week') }, lineColor: GRAY_400_COLOUR, labels: { formatter: function(item) { let date = new Date(item.value); return `${date.getDate()}-${getMonthShortName(date)}` }, style: { color: 'black' } } },
					yAxis: { title: { text: `${this.$t('total')} ${this.$t('weeks')}` }, gridLineColor: GRAY_400_COLOUR, labels: { style: { color: 'black' } }, min: 0 },
					credits: false,
					chart: { backgroundColor: BACKGROUND_COLOUR },
					series: [{
						type: 'column',
						color: this.$store.state.healthyHabits.chart.color,
						dataLabels: [{ color: 'white', enabled: true, inside: true }],
						data: this.getAccumulatedByWeeks(),
						name: this.$store.state.healthyHabits.chart.title,
						tooltip: { headerFormat: '', pointFormatter: function () { return `${_this.getHistoryHeader(this)}` } }
					}]
				}
			}
		},
		getFrequencyOptions (refresh) {
			let isEmpty = Object.keys(this.$store.state.healthyHabits.chart.frequency.options).length === 0
			if (refresh && !isEmpty) {
				this.$store.state.healthyHabits.chart.frequency.options.series[0].data = this.getFrequencyData()
			} else {
				this.$store.state.healthyHabits.chart.frequency.options = {
					title: { text: this.$t('frequency'), style: { color: this.$store.state.healthyHabits.chart.color } },
					xAxis: { categories: this.getLastYearMonthNamesShort(), lineColor: GRAY_400_COLOUR },
					yAxis: { categories: getWeekDaysShortNames().reverse(), gridLineColor: GRAY_400_COLOUR, max: 6, min: 0, opposite: true, title: {text: null} },
					credits: false,
					plotOptions: { bubble: { displayNegative: false } },
					chart: { backgroundColor: BACKGROUND_COLOUR },
					series: [{
						type: 'bubble',
						color: this.$store.state.healthyHabits.chart.color,
						sizeByAbsoluteValue: true,
						dataLabels: [{ enabled: false }],
						data: this.getFrequencyData(),
						name: this.$store.state.healthyHabits.chart.title,
						maxSize: '9%',
						tooltip: { headerFormat: '', pointFormatter: function () { return this.dayAccumulated } }
					}]
				}
			}
		},
		getScoreOptions (refresh) {
			let scoreData = this.getScoreData()
			this.$store.state.healthyHabits.chart.score.dataCache = scoreData
			let isEmpty = Object.keys(this.$store.state.healthyHabits.chart.score.options).length === 0
			if (refresh && !isEmpty) {
				this.scoreIntervalSelected(this.$store.state.healthyHabits.chart.score.interval)
			} else {
				this.$store.state.healthyHabits.chart.score.options = {
					id: 'score-chart',
					title: { text: this.$t('score'), style: { color: this.$store.state.healthyHabits.chart.color } },
					xAxis: { title: { text: this.$t('day') }, tickInterval: 24 * 3600 * 1000, tickPositions: scoreData.map(item => item.x), lineColor: GRAY_400_COLOUR, labels: { formatter: function(item) { return `${formatDateForGraphAxis(new Date(item.value))}` }, style: { color: 'black' } } },
					yAxis: { title: { text: this.$t('percentage') }, gridLineColor: GRAY_400_COLOUR, labels: { style: { color: 'black' }, format: `{value}%` }, max: 100, min: 0, tickInterval: 10 },
					credits: false,
					plotOptions: {
						scatter: {
							lineWidth: 2
						}
					},
					chart: { backgroundColor: BACKGROUND_COLOUR },
					series: [{
						type: 'scatter',
						color: this.$store.state.healthyHabits.chart.color,
						data: scoreData,
						name: this.$store.state.healthyHabits.chart.title,
						tooltip: { headerFormat: '', pointFormatter: function () { return `${this.y}%` } }
					}]
				}
			}
			this.$store.state.healthyHabits.chart.score.currentValue = scoreData[scoreData.length - 1].y
		},
		historyIntervalSelected (value) {
			switch (value) {
				case 1:
					this.$store.state.healthyHabits.chart.history.options.xAxis.title.text = this.$t('week')
					this.$store.state.healthyHabits.chart.history.options.xAxis.labels.formatter = function (item) { return formatDateForGraphAxis(new Date(item.value)) }
					this.$store.state.healthyHabits.chart.history.options.yAxis.title.text = `${this.$t('total')} ${this.$t('weeks')}`
					this.$store.state.healthyHabits.chart.history.options.series[0].data = this.getAccumulatedByWeeks()
					break
				case 2:
					this.$store.state.healthyHabits.chart.history.options.xAxis.title.text = this.$t('month')
					this.$store.state.healthyHabits.chart.history.options.xAxis.labels.formatter = function (item) { return getMonthName(new Date(item.value)) }
					this.$store.state.healthyHabits.chart.history.options.yAxis.title.text = `${this.$t('total')} ${this.$t('monthsTitle')}`
					this.$store.state.healthyHabits.chart.history.options.series[0].data = this.getAccumulatedByMonths()
					break
				case 3:
					this.$store.state.healthyHabits.chart.history.options.xAxis.title.text = this.$t('year')
					this.$store.state.healthyHabits.chart.history.options.xAxis.labels.formatter = function (item) { let date = new Date(item.value); return date.getFullYear() }
					this.$store.state.healthyHabits.chart.history.options.yAxis.title.text = `${this.$t('total')} ${this.$t('yearsTitle')}`
					this.$store.state.healthyHabits.chart.history.options.series[0].data = this.getAccumulatedByYears()
					break
			}
			this.updateChartKey('history')
		},
		scoreIntervalSelected (value) {
			switch (value) {
				case 0:
					this.$store.state.healthyHabits.chart.score.options.xAxis.title.text = this.$t('day')
					this.$store.state.healthyHabits.chart.score.options.xAxis.labels.formatter = function (item) { return formatDateForGraphAxis(new Date(item.value)) }
					this.$store.state.healthyHabits.chart.score.options.series[0].data = this.getScoreData()
					this.$store.state.healthyHabits.chart.score.options.xAxis.tickPositions = this.$store.state.healthyHabits.chart.score.options.series[0].data.map(item => item.x)
					break
				case 1:
					this.$store.state.healthyHabits.chart.score.options.xAxis.title.text = this.$t('week')
					this.$store.state.healthyHabits.chart.score.options.xAxis.labels.formatter = function (item) { return formatDateForGraphAxis(new Date(item.value)) }
					this.$store.state.healthyHabits.chart.score.options.series[0].data = this.getScoreDataByWeeks()
					this.$store.state.healthyHabits.chart.score.options.xAxis.tickPositions = this.$store.state.healthyHabits.chart.score.options.series[0].data.map(item => item.x)
					break
				case 2:
					this.$store.state.healthyHabits.chart.score.options.xAxis.title.text = this.$t('month')
					this.$store.state.healthyHabits.chart.score.options.xAxis.labels.formatter = function (item) { return getMonthName(new Date(item.value)) }
					this.$store.state.healthyHabits.chart.score.options.series[0].data = this.getScoreDataByMonths()
					this.$store.state.healthyHabits.chart.score.options.xAxis.tickPositions = this.$store.state.healthyHabits.chart.score.options.series[0].data.map(item => item.x)
					break
				case 3:
					this.$store.state.healthyHabits.chart.score.options.xAxis.title.text = this.$t('year')
					this.$store.state.healthyHabits.chart.score.options.xAxis.labels.formatter = function (item) { let date = new Date(item.value); return date.getFullYear() }
					this.$store.state.healthyHabits.chart.score.options.series[0].data = this.getScoreDataByYears()
					this.$store.state.healthyHabits.chart.score.options.xAxis.tickPositions = this.$store.state.healthyHabits.chart.score.options.series[0].data.map(item => item.x)
					break
			}
			this.updateChartKey('score')
		},
		updateChartKey (id) {
			this.$store.state.healthyHabits.chart[id].key++
		},
		getHistoryHeader (data) {
			let date = new Date(data.x)
			switch (this.$store.state.healthyHabits.chart.history.interval) {
				case 1:
					let startDate = this.getDateOnlySpanishFormat(date)
					date.setDate(date.getDate() + 6)
					let finishDate = this.getDateOnlySpanishFormat(date)
					return `${startDate} ${this.$t('to')} ${finishDate}`
				case 2:
					return getMonthName(date)
				case 3:
					return date.getFullYear()
			}
		},
		resetItemSelected () {
			this.$store.state.healthyHabits.itemSelected.date = null
			this.$store.state.healthyHabits.itemSelected.currentValue = null
			this.$store.state.healthyHabits.itemSelected.newValue = null
			this.$store.state.healthyHabits.itemSelected.measureUnit = null
		},
		updateGoalStreak (fromCalendar, refreshData) {
			let itemModified = fromCalendar ? this.$store.state.healthyHabits.chart.dataMapped : this.$store.state.healthyHabits.table.data.items.find(item => item.id === this.$store.state.healthyHabits.table.itemSelectedId)
			if (!refreshData) {
					itemModified.total = Object.keys(itemModified).filter(key => this.checkValidDate(key) && ((!itemModified.measurable && itemModified[key] === true) || (itemModified.measurable && itemModified[key] > 0))).length
			}
			itemModified.currentStreak = this.getCurrentStreak(itemModified.measurable ? this.fillMeasurablePartialGoalCompleted(itemModified) : itemModified)
			itemModified.bestStreak = this.getBestStreak(itemModified.measurable ? this.fillMeasurablePartialGoalCompleted(itemModified) : itemModified, itemModified.currentStreak)
			let currentScore = 0
			if (!fromCalendar) {
				this.$store.state.healthyHabits.bestStreak = Math.max(...this.$store.state.healthyHabits.table.data.items.map(item => item.bestStreak))
				let scoreData = this.getScoreData(itemModified)
				currentScore = scoreData[scoreData.length - 1].y
			} else {
				this.$store.state.healthyHabits.chart.total = itemModified.total
				currentScore = this.$store.state.healthyHabits.chart.score.currentValue
			}
			let goalCompleted = null
			if (refreshData) {
				goalCompleted = currentScore === 100
				this.$store.state.healthyHabits.showGoalCompletedModal = goalCompleted
				itemModified.goalCompleted = goalCompleted
			} else if (currentScore === 100 && !itemModified.goalCompleted) {
				this.$store.state.healthyHabits.showGoalCompletedModal = true
				goalCompleted = true
				itemModified.goalCompleted = true
				if (!fromCalendar) {
					this.$store.state.healthyHabits.table.data.items = this.$store.state.healthyHabits.table.data.items.filter(h => h.id !== itemModified.id)
				}
			}
			return this.updateTableData({ itemId: this.$store.state.healthyHabits.table.itemSelectedId, bestStreak: itemModified.bestStreak, currentStreak: itemModified.currentStreak, total: itemModified.total, goalCompleted: goalCompleted }).then((response) => {
				this.$store.state.healthyHabits.hideLoadingIcon = false
				this.resetItemSelected()
				if (refreshData) {
					this.getHealthyHabitsData()
				}
			})
		},
		fillMeasurablePartialGoalCompleted (dataInput) {
			let data = Object.assign({}, dataInput)
			let dataKeys = Object.keys(data)
			let dateNow = this.getCurrentDateUtc()
			let minDateInput = new Date(Math.min(...dataKeys.filter(key => this.checkValidDate(key)).map(date => new Date(date))))
			let clearPartialValues = dataKeys.filter(key => this.checkValidDate(key) && (data[key] === -1 || new Date(key) > this.getCurrentDateUtc()))
			if (clearPartialValues.length > 0) {
				for (let index in clearPartialValues) {
					let key = clearPartialValues[index]
					delete data[key]
				}
			}
			if (data.goalFrequencyId == WEEKLY_GOAL) {
				let maxDate = dateNow
				let minDate = minDateInput
				let weekDateLimit = new Date(this.getFirstWeekDate(minDate))
				let lastDate = maxDate < this.getCurrentDateUtc() ? this.getLastDateInWeek(maxDate) : maxDate
				let maxDateWeek = new Date(new Date(weekDateLimit).setDate(weekDateLimit.getDate() + 6))
				while (weekDateLimit <= lastDate) {
					let datesFilteredCurrentWeek = dataKeys.filter(key => this.checkValidDate(key) && maxDateWeek >= new Date(key) && new Date(key) >= weekDateLimit).sort((a, b) => new Date(a) - new Date(b))
					if (datesFilteredCurrentWeek.length > 0) {
						let datesFilteredCompleted = datesFilteredCurrentWeek.reduce((acc, date) => acc + data[date], 0)
						let goalCompleted = datesFilteredCompleted >= data.goal
						while (weekDateLimit <= maxDateWeek) {
							let currentDateFormatted = this.getDateOnlyFormatted(weekDateLimit)
							if ((data[currentDateFormatted] < data.goal || !data[currentDateFormatted]) && goalCompleted) {
								data[currentDateFormatted] = -1
							}
							weekDateLimit.setDate(weekDateLimit.getDate() + 1)
						}
					} else {
						weekDateLimit.setDate(weekDateLimit.getDate() + 7)
					}
					maxDateWeek = new Date(new Date(maxDateWeek).setDate(maxDateWeek.getDate() + 7))
				}
			} else if (data.goalFrequencyId == MONTHLY_GOAL) {
				let maxDate = dateNow
				maxDate = maxDate > dateNow ? dateNow : maxDate
				let limitDate = new Date(new Date(minDateInput.getFullYear(), minDateInput.getMonth(), 1).setHours(0, 0, 0, 0))
				while (limitDate <= maxDate) {
					let lastMonth = this.getDateOnlyFormatted(limitDate).split('-')[1]
					let lastYear = this.getDateOnlyFormatted(limitDate).split('-')[0]
					let lastMonthDate = new Date(new Date(lastYear, lastMonth, 0).setHours(0, 0, 0, 0))
					let datesFiltered = dataKeys.filter(key => this.checkValidDate(key) && lastMonthDate >= new Date(key) && new Date(key) >= limitDate).sort((a, b) => new Date(a) - new Date(b))
					if (datesFiltered.length > 0) {
						let datesFilteredCompleted = datesFiltered.reduce((acc, date) => acc + data[date], 0)
						let goalCompleted = datesFilteredCompleted >= data.goal
						while (limitDate <= lastMonthDate) {
							let currentDateFormatted = this.getDateOnlyFormatted(limitDate)
							if ((data[currentDateFormatted] < data.goal || !data[currentDateFormatted]) && goalCompleted) {
								data[currentDateFormatted] = -1
							}
							limitDate.setDate(limitDate.getDate() + 1)
						}
					} else {
						limitDate = new Date(new Date(limitDate.getFullYear(), limitDate.getMonth() + 1, 1).setHours(0, 0, 0, 0))
					}
				}
			}
			return data
		},
		getCurrentStreak (data) {
			let dataKeys = Object.keys(data)
			let dates = dataKeys.filter(key => (this.checkValidDate(key) && new Date(key) <= this.getCurrentDateUtc() && ((data[key] === true || data[key] === null || data[key] === -1) || (data.measurable && (data[key] > 0 || data[key] === -1))))).map(date => {return {'date': new Date(date), value: data[date] || data[date] === -1 ? 1 : 0}}).sort((a, b) => b.date - a.date)
			let currentDate = this.getCurrentDateUtc()
			let currentConsecutiveDays = 0
			for (let index in dates) {
				let date = dates[index].date
				let value = dates[index].value
				if (this.getDateOnlyFormatted(date) === this.getDateOnlyFormatted(currentDate)) {
					currentConsecutiveDays = value ? ++currentConsecutiveDays : currentConsecutiveDays
					currentDate.setDate(currentDate.getDate() - 1)
				} else if (index == 0) {
					currentDate.setDate(currentDate.getDate() - 1)
					if (this.getDateOnlyFormatted(currentDate) === this.getDateOnlyFormatted(date)) {
						currentConsecutiveDays = value ? ++currentConsecutiveDays : currentConsecutiveDays
						currentDate.setDate(currentDate.getDate() - 1)
					}
				} else {
					if (index > 0) {
						return currentConsecutiveDays
					}
					currentDate.setDate(currentDate.getDate()-1)
				}
			}
			return currentConsecutiveDays
		},
		getBestStreak (data, currentStreak) {
			let dataKeys = Object.keys(data)
			let dates = dataKeys.filter(key => this.checkValidDate(key) && ((data[key] === true || data[key] === null || data[key] === -1) || (data.measurable && (data[key] > 0 || data[key] === -1)))).map(date => {return {'date': new Date(date), value: data[date] || data[date] === -1 ? 1 : 0}}).sort((a, b) => b.date - a.date)
			let maxConsecutiveDays = 0
			let currentConsecutiveDays = 0
			for (let i = 1; i < dates.length; i++) {
				let prevDate = dates[i - 1].date
				let prevDateValue = dates[i - 1].value
				let date = dates[i].date
				let dateValue = dates[i].value
				let diffTime = Math.abs(date - prevDate)
				let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
				if (diffDays === 1) {
					currentConsecutiveDays += (currentConsecutiveDays == 0 ? prevDateValue + dateValue : dateValue)
					maxConsecutiveDays = Math.max(maxConsecutiveDays, currentConsecutiveDays)
				} else {
					currentConsecutiveDays = 0
				}
			}
			if (maxConsecutiveDays === 0 && dataKeys.some(key => this.checkValidDate(key) && (data[key] === true || data[key] > 0))) {
				return 1
			}
			return maxConsecutiveDays > currentStreak ? maxConsecutiveDays : currentStreak
		},
		getCurrentStreakOnStart (data, savedStreak) {
			let now = this.getDateOnlyFormatted(new Date())
			let yesterday = this.getDateOnlyFormatted(new Date().setDate(new Date().getDate() - 1))
			let lastTwoDatesUnfilled = Object.keys(data).some(key => this.checkValidDate(key) && ((data[now] == 0 || data[now] === undefined || data[now] === false) && (data[yesterday] == 0 || data[yesterday] === undefined || data[yesterday] === false)))
			if (lastTwoDatesUnfilled) {
				return 0
			}
			return savedStreak
		},
		getTotalCountWeek (data, percentage) {
			data = data || this.$store.state.healthyHabits.chart.dataMapped
			let dataKeys = Object.keys(data)
			let inputDate = this.getCurrentDateUtc()
			let inputDateDay = inputDate.getDay() == 0 ? 6 : inputDate.getDay() - 1
			let endDate = new Date(inputDate).setDate(inputDate.getDate() + (6 - inputDateDay))
			if (endDate > inputDate) {
				endDate = new Date(inputDate)
			}
			let currentDate = new Date(inputDate.setDate(inputDate.getDate() - inputDateDay))
			let count = dataKeys.reduce((acc, date) => { if (this.checkValidDate(date) && data[date] && currentDate <= new Date(date) && new Date(date) <= endDate) { return data.measurable && !percentage ? acc += data[date] : acc += 1 } else { return acc } }, 0)
			if (percentage) {
				return Math.round((count / 7) * 100) > 100 ? 100 : Math.round((count / 7) * 100)
			} else {
				return count
			}
		},
		getTotalCountMonth (data, percentage) {
			data = data || this.$store.state.healthyHabits.chart.dataMapped
			let dataKeys = Object.keys(data)
			let firstMonthDate = new Date(new Date().setDate(1)).setHours(0, 0, 0, 0)
			let currentMonth = this.getDateOnlyFormatted(firstMonthDate).split('-')[1]
			let currentYear = this.getDateOnlyFormatted(firstMonthDate).split('-')[0]
			let lastMonthDate = new Date(new Date(currentYear, currentMonth, 0).setHours(0, 0, 0, 0))
			let count = dataKeys.reduce((acc, date) => { if (this.checkValidDate(date) && data[date] && firstMonthDate <= new Date(date) && new Date(date) <= lastMonthDate) { return data.measurable && !percentage ? acc += data[date] : acc += 1} else { return acc } }, 0)
			if (percentage) {
				return Math.round((count / lastMonthDate.getDate()) * 100) > 100 ? 100 : Math.round((count / lastMonthDate.getDate()) * 100)
			} else {
				return count
			}
		},
		getTotalCountYear (data, percentage) {
			data = data || this.$store.state.healthyHabits.chart.dataMapped
			let dataKeys = Object.keys(data)
			let firstYearDate = new Date(new Date(new Date().getFullYear(), 0, 1).setHours(0, 0, 0, 0))
			let lastYearDate = new Date(new Date(new Date().getFullYear(), 11, 31).setHours(0, 0, 0, 0))
			let count = dataKeys.reduce((acc, date) => { if (this.checkValidDate(date) && data[date] && firstYearDate <= new Date(date) && new Date(date) <= lastYearDate) { return data.measurable && !percentage ? acc += data[date] : acc += 1 } else { return acc } }, 0)
			if (percentage) {
				return Math.round((count / this.daysInYear(firstYearDate.getFullYear())) * 100) > 100 ? 100 : Math.round((count / this.daysInYear(firstYearDate.getFullYear())) * 100)
			} else {
				return count
			}
		},
		sendModifiedData () {
			this.$store.state.healthyHabits.table.updatingItem = true
			this.$store.state.healthyHabits.hideLoadingIcon = true
			let itemModified = !this.$store.state.healthyHabits.itemSelected.fromCalendar ? this.$store.state.healthyHabits.table.data.items.find(item => item.id === this.$store.state.healthyHabits.table.itemSelectedId) : null
			this.updateTableData({ itemId: this.$store.state.healthyHabits.table.itemSelectedId, date: this.$store.state.healthyHabits.itemSelected.date, value: this.$store.state.healthyHabits.itemSelected.newValue, returnAllData: !this.$store.state.healthyHabits.itemSelected.fromCalendar }).then((response) => {
				if (response.data) {
					if (this.$store.state.healthyHabits.itemSelected.fromCalendar) {
						this.processHealthyHabitsDataForStatistics()
					} else {
						itemModified[this.$store.state.healthyHabits.itemSelected.date] = itemModified.measurable ? parseFloat(this.$store.state.healthyHabits.itemSelected.newValue) : this.$store.state.healthyHabits.itemSelected.newValue
						let dataKeys = Object.keys(itemModified)
						if (response.data.data) {
							let serverData = response.data.data.filter(item => !dataKeys.some(key => key === this.getDateOnlyFormatted(item.date)))
							for (let index in serverData) {
								let item = serverData[index]
								if (itemModified.measurable) {
									itemModified[this.getDateOnlyFormatted(item.date)] = parseFloat(item.value)
								} else {
									itemModified[this.getDateOnlyFormatted(item.date)] = item.value === 'true' ? true : item.value === 'false' || item.value === 0 ? false : null
								}
							}
							dataKeys = Object.keys(itemModified)
						}
						let dataMapped = dataKeys.filter(key => this.checkValidDate(key)).map(date => new Date(date))
						if (!itemModified.measurable) {
							this.fillPartialGoalCompleted(itemModified, new Date(Math.min(...dataMapped)))
						}
						itemModified.accumulatedGoal = this.lastGoalAccumulated(itemModified)
						this.updateGoalStreak().then(() => {
							this.$store.state.healthyHabits.table.key++
							this.$store.state.healthyHabits.table.updatingItem = false
						})
					}
				} else {
					this.$store.dispatch('showDangerAlert', this.$t('defaultErrorMessage'))
					this.$store.state.healthyHabits.table.updatingItem = false
					this.$store.state.healthyHabits.hideLoadingIcon = false
				}
			})
		},
		updateSetting () {
			if (this.$validators.validate(this.$store.state.healthyHabits.setting.notifications.email, ['email'], true) === false) {
				this.$store.dispatch('showInfoAlert', this.$t('insertValidEmail'))
				return
			} else if (this.$store.state.healthyHabits.setting.notifications.phone && this.$validators.validate(this.$store.state.healthyHabits.setting.notifications.phone, ['phone'], true) === false) {
				this.$store.dispatch('showInfoAlert', this.$t('insertValidPhone'))
				return
			}
			let data = {
				nick: this.$store.state.healthyHabits.setting.nick,
				notificationsEmail: this.$store.state.healthyHabits.setting.notifications.email,
				notificationsPhone: this.$store.state.healthyHabits.setting.notifications.phone,
				showStoredHabits: this.$store.state.healthyHabits.setting.showStoredHabits,
				showGoalCompletedHabits: this.$store.state.healthyHabits.setting.showGoalCompletedHabits,
				allowFreeDays: this.$store.state.healthyHabits.setting.allowFreeDays,
				invertedCalendarDays: this.$store.state.healthyHabits.setting.invertedCalendarDays
			}
			this.processPromise(healthyHabitsRepository.updateSetting(data.nick, data.notificationsEmail, data.notificationsPhone, data.showStoredHabits, data.showGoalCompletedHabits, data.allowFreeDays, data.invertedCalendarDays)).then((response) => {
				if (response.data) {
					this.$store.state.healthyHabits.setting.showModal = false
					if (this.$route.params.id && this.$store.state.healthyHabits.setting.originalAllowFreeDays !== this.$store.state.healthyHabits.setting.allowFreeDays) {
						this.$store.state.healthyHabits.itemSelected.fromCalendar = true
						this.getHealthyHabitsStatistics(this.$route.params.id)
					} else if (!this.$route.params.id && (this.$store.state.healthyHabits.setting.originalAllowFreeDays !== this.$store.state.healthyHabits.setting.allowFreeDays || this.$store.state.healthyHabits.setting.originalShowStoredHabits !== this.$store.state.healthyHabits.setting.showStoredHabits || this.$store.state.healthyHabits.setting.originalShowGoalCompletedHabits !== this.$store.state.healthyHabits.setting.showGoalCompletedHabits)) {
						this.getHealthyHabitsData(this.$store.state.healthyHabits.setting.originalAllowFreeDays !== this.$store.state.healthyHabits.setting.allowFreeDays)
					}
					this.$store.state.healthyHabits.setting.originalAllowFreeDays = this.$store.state.healthyHabits.setting.allowFreeDays
					this.$store.state.healthyHabits.setting.originalShowStoredHabits = this.$store.state.healthyHabits.setting.showStoredHabits
					this.$store.state.healthyHabits.setting.originalShowGoalCompletedHabits = this.$store.state.healthyHabits.setting.showGoalCompletedHabits
					this.updateHeaderFields()
					this.$store.dispatch('showSuccessAlert', this.$t('changesPerformedCorrectly'))
				} else {
					this.$store.dispatch('showDangerAlert', this.$t('defaultErrorMessage'))
				}
			})
		},
		updateHeaderFields () {
			let fields = this.$store.state.healthyHabits.table.data.fields = [{ key: 'description', label: '' }, { key: 'nextDates', label: '' }, ...this.getHeaderDatesInIntervalFormatted(this.$store.state.healthyHabits.table.minDate, this.$store.state.healthyHabits.table.maxDate), { key: 'prevDates', label: '' }, { key: 'actions', label: '' }]
			// if (!this.mobileVersion()) fields = [...fields, { key: 'sliderSection1', label: '' }, { key: 'sliderSection2', label: '' }, { key: 'sliderSection3', label: '' }]
			this.$store.state.healthyHabits.table.data.fields = fields
		},
		mobileVersion () {
			return window.screen.width <= this.$store.state.healthyHabits.mobileSizeLimit
		},
		getDateNameFieldFormatted (date) {
			return getWeekDayShortName(new Date(date))
		},
		getDateFieldFormatted (date) {
			return new Date(date).getDate()
		},
		tableNumberDaysVisible () {
			return this.mobileVersion() ? 2 : 5
		},
		fillDatesDefaultValue (data) {
			let currentDate = new Date(data[0].date)
			let result = []
			while (currentDate <= this.getCurrentDateUtc()) {
				if (!data.some((item) => this.getDateOnlyFormatted(item.date) === this.getDateOnlyFormatted(currentDate))) {
					result.push({ date: new Date(currentDate), value: 0 })
				}
				currentDate.setDate(currentDate.getDate() + 1)
			}
			data = data.concat(result)
			data.sort((a, b) => b.date - a.date)
			return data
		},
		lastGoalAccumulated (data) {
			let dataKeys = Object.keys(data)
			let dates = []
			// measurable: daily, weekly and monthy; not measurable: each x days, x times at week or month
			if (data.goalFrequencyId == DAILY_GOAL) {
				let finishDate = this.getCurrentDateUtc()
				let startDate = this.getCurrentDateUtc().setDate(new Date().getDate() - data.goal)
				dates = dataKeys.filter(key => this.checkValidDate(key) && (new Date(key) <= finishDate && (data.measurable ? new Date(key) >= this.getCurrentDateUtc() : new Date(key) > startDate)))
			}
			if (data.goalFrequencyId == WEEKLY_GOAL) {
				dates = dataKeys.filter(key => this.checkValidDate(key) && this.getActualWeekDatesFormatted().some(item => item === key))
			}
			if (data.goalFrequencyId == MONTHLY_GOAL) {
				let currentMonth = new Date().getMonth() + 1
				dates = dataKeys.filter(key => this.checkValidDate(key) && currentMonth == parseFloat(key.split('-')[1]))
			}
			let result = data.measurable ? dates.reduce((acc, date) => acc + data[date], 0) : data.goalFrequencyId == DAILY_GOAL ? (dates.some(date => data[date] === true) ? data.goal : 0) : dates.filter(date => data[date] === true).length
			return result
		},
		fillPartialGoalCompleted (data, minDateInput) {
			let dataKeys = Object.keys(data)
			let dateNow = this.getCurrentDateUtc()
			let clearPartialValues = dataKeys.filter(key => this.checkValidDate(key) && (data[key] === -1 || new Date(key) > this.getCurrentDateUtc()))
			if (clearPartialValues.length > 0) {
				for (let index in clearPartialValues) {
					let key = clearPartialValues[index]
					delete data[key]
				}
			}
			if (data.goalFrequencyId == DAILY_GOAL) {
				if (data.goal > 1) {
					let maxDate = dateNow
					let minDate = minDateInput ? new Date(minDateInput) : new Date(this.$store.state.healthyHabits.table.minDateServer)
					minDate.setDate(minDate.getDate() - data.goal)
					let datesFiltered = dataKeys.filter(key => this.checkValidDate(key) && maxDate >= new Date(key) && new Date(key) >= minDate && data[key] === true).sort((a, b) => new Date(b) - new Date(a))
					if (datesFiltered.length > 0) {
						for (let item in datesFiltered) {
							let currentItem = datesFiltered[item]
							let currentDate = new Date(new Date(currentItem).setDate(new Date(currentItem).getDate() + 1))
							let finishDate = new Date(new Date(currentDate).setDate(new Date(currentDate).getDate() + (data.goal - 2)))
							let goalCount = 0
							let fillInverse = false
							while (currentDate <= finishDate) {
								let key = this.getDateOnlyFormatted(currentDate)
								if (data[key] === true || data[key] === -1) {
									fillInverse = true
									break
								}
								if (data[key] !== null) {
									data[key] = -1 
								}
								goalCount++
								currentDate.setDate(currentDate.getDate() + 1)
							}
							if (fillInverse) {
								let prevDates = data.goal - goalCount - 1
								currentDate = new Date(new Date(currentItem).setDate(new Date(currentItem).getDate() - 1))
								while (prevDates > 0) {
									let key = this.getDateOnlyFormatted(currentDate)
									if (data[key] === true || data[key] === -1) {
										break
									}
									data[key] = data[key] === false || data[key] === undefined ? -1 : data[key]
									prevDates--
									currentDate.setDate(currentDate.getDate() - 1)
								}
							}
						}
					}
				}
			} else if (data.goalFrequencyId == WEEKLY_GOAL) {
				let maxDate = dateNow
				let minDate = minDateInput ? new Date(minDateInput) : this.$store.state.healthyHabits.table.minDateServer
				let weekDateLimit = new Date(this.getFirstWeekDate(minDate))
				let lastDate = maxDate < this.getCurrentDateUtc() ? this.getLastDateInWeek(maxDate) : maxDate
				let maxDateWeek = new Date(new Date(weekDateLimit).setDate(weekDateLimit.getDate() + 6))
				while (weekDateLimit <= lastDate) {
					let datesFilteredCurrentWeek = dataKeys.filter(key => this.checkValidDate(key) && maxDateWeek >= new Date(key) && new Date(key) >= weekDateLimit).sort((a, b) => new Date(a) - new Date(b))
					if (datesFilteredCurrentWeek.length > 0) {
						let datesFilteredCompleted = datesFilteredCurrentWeek.filter(key => data[key] === true)
						let goalCompleted = datesFilteredCompleted.length >= data.goal
						while (weekDateLimit <= maxDateWeek) {
							let currentDateFormatted = this.getDateOnlyFormatted(weekDateLimit)
							data[currentDateFormatted] = datesFilteredCompleted.some(item => item === currentDateFormatted) ? true : data[currentDateFormatted] === null ? null : goalCompleted ? -1 : false
							weekDateLimit.setDate(weekDateLimit.getDate() + 1)
						}
					} else {
						weekDateLimit.setDate(weekDateLimit.getDate() + 7)
					}
					maxDateWeek = new Date(new Date(maxDateWeek).setDate(maxDateWeek.getDate() + 7))
				}
			} else if (data.goalFrequencyId == MONTHLY_GOAL) {
				let maxDate = dateNow
				maxDate = maxDate > dateNow ? dateNow : maxDate
				let limitDate = minDateInput ? new Date(new Date(minDateInput.getFullYear(), minDateInput.getMonth(), 1).setHours(0, 0, 0, 0)) : new Date(new Date(this.$store.state.healthyHabits.table.minDateServer.getFullYear(), this.$store.state.healthyHabits.table.minDateServer.getMonth(), 1).setHours(0, 0, 0, 0))
				while (limitDate <= maxDate) {
					let lastMonth = this.getDateOnlyFormatted(limitDate).split('-')[1]
					let lastYear = this.getDateOnlyFormatted(limitDate).split('-')[0]
					let lastMonthDate = new Date(new Date(lastYear, lastMonth, 0).setHours(0, 0, 0, 0))
					let datesFiltered = dataKeys.filter(key => this.checkValidDate(key) && lastMonthDate >= new Date(key) && new Date(key) >= limitDate).sort((a, b) => new Date(a) - new Date(b))
					if (datesFiltered.length > 0) {
						let datesFilteredCompleted = datesFiltered.filter(key => data[key] === true)
						let goalCompleted = datesFilteredCompleted.length >= data.goal
						while (limitDate <= lastMonthDate) {
							let currentDateFormatted = this.getDateOnlyFormatted(limitDate)
							data[currentDateFormatted] = datesFilteredCompleted.some(item => item === currentDateFormatted) ? true : data[currentDateFormatted] === null ? null : goalCompleted ? -1 : false
							limitDate.setDate(limitDate.getDate() + 1)
						}
					} else {
						limitDate = new Date(new Date(limitDate.getFullYear(), limitDate.getMonth() + 1, 1).setHours(0, 0, 0, 0))
					}
				}
			}
		},
		fillPartialGoalCompletedCalendar (data, minDateInput) {
			let dataKeys = Object.keys(data)
			if (data.goalFrequencyId == DAILY_GOAL) {
				let maxDate = this.getCurrentDateUtc()
				let minDate = new Date(new Date(minDateInput).setHours(0, 0, 0, 0))
				let limitDate = new Date(minDate)
				minDate.setDate(minDateInput.getDate() - data.goal)
				let datesFiltered = dataKeys.filter(key => this.checkValidDate(key) && maxDate >= new Date(key) && new Date(key) >= minDate && data[key] === true).sort((a, b) => new Date(b) - new Date(a))
				if (datesFiltered.length > 0) {
					for (let item in datesFiltered) {
						let currentItem = datesFiltered[item]
						let currentDate = new Date(new Date(currentItem).setDate(new Date(currentItem).getDate() + 1))
						let finishDate = new Date(currentDate).setDate(new Date(currentDate).getDate() + (data.goal - 2))
						let goalCount = 0
						let fillInverse = false
						while (currentDate <= finishDate) {
							let key = this.getDateOnlyFormatted(currentDate)
							if (data[key] === true || data[key] === -1) {
								fillInverse = true
								break
							}
							data[key] = data[key] !== true && data[key] !== null ? -1 : data[key]
							goalCount++
							currentDate.setDate(currentDate.getDate() + 1)
						}
						if (fillInverse) {
							let iterations = data.goal - goalCount - 1
							currentDate = new Date(new Date(currentItem).setDate(new Date(currentItem).getDate() - 1))
							while (iterations > 0) {
								let key = this.getDateOnlyFormatted(currentDate)
								data[key] = data[key] === false || data[key] === undefined ? -1 : data[key]
								iterations--
								currentDate.setDate(currentDate.getDate() - 1)
							}
						}
					}
				} else {
					while (limitDate <= maxDate) {
						let key = this.getDateOnlyFormatted(limitDate)
						data[key] = data[key] === -1 ? false : data[key]
						limitDate.setDate(limitDate.getDate() + 1)
					}
				}
			} else if (data.goalFrequencyId == WEEKLY_GOAL) {
				let maxDate = this.getCurrentDateUtc()
				let lastDayOfWeek = this.getLastDateInWeek(maxDate)
				maxDate = lastDayOfWeek
				let minDate = new Date(minDateInput.setHours(0, 0, 0, 0))
				let limitDate = this.getFirstWeekDate(minDate)
				let weekDateLimit = new Date(limitDate)
				weekDateLimit = new Date(weekDateLimit.setDate(weekDateLimit.getDate() + 6))
				while (weekDateLimit <= maxDate) {
					let datesFilteredFirstWeek = dataKeys.filter(key => this.checkValidDate(key) && weekDateLimit >= new Date(key) && new Date(key) >= limitDate).sort((a, b) => new Date(a) - new Date(b))
					if (datesFilteredFirstWeek.length > 0) {
						let datesFilteredCompleted = datesFilteredFirstWeek.filter(key => data[key] === true)
						let goalCompleted = datesFilteredCompleted.length >= data.goal
						let currentDate = new Date(limitDate)
						while (currentDate <= weekDateLimit) {
							let currentDateFormatted = this.getDateOnlyFormatted(currentDate)
							data[currentDateFormatted] = datesFilteredCompleted.some(item => item === currentDateFormatted) ? true : data[currentDateFormatted] === null ? null : goalCompleted ? -1 : false
							currentDate.setDate(currentDate.getDate() + 1)
						}
					}
					limitDate = new Date(weekDateLimit)
					limitDate.setDate(limitDate.getDate() + 1)
					weekDateLimit.setDate(weekDateLimit.getDate() + 7)
				}
			} else if (data.goalFrequencyId == MONTHLY_GOAL) {
				let maxDate = this.getCurrentDateUtc()
				let limitDate = new Date(minDateInput.setHours(0, 0, 0, 0))
				while (limitDate <= maxDate) {
					let firstMonthDate = new Date(new Date(limitDate).setDate(1)).setHours(0, 0, 0, 0)
					let lastMonth = this.getDateOnlyFormatted(limitDate).split('-')[1]
					let lastYear = this.getDateOnlyFormatted(limitDate).split('-')[0]
					let lastMonthDate = new Date(new Date(lastYear, lastMonth, 0).setHours(0, 0, 0, 0))
					let datesFilteredFirstMonth = dataKeys.filter(key => this.checkValidDate(key) && lastMonthDate >= new Date(key) && new Date(key) >= firstMonthDate).sort((a, b) => new Date(a) - new Date(b))
					if (datesFilteredFirstMonth.length > 0) {
						let datesFilteredCompleted = datesFilteredFirstMonth.filter(key => data[key] === true)
						let goalCompleted = datesFilteredCompleted.length >= data.goal
						while (limitDate <= lastMonthDate) {
							let currentDateFormatted = this.getDateOnlyFormatted(limitDate)
							data[currentDateFormatted] = datesFilteredCompleted.some(item => item === currentDateFormatted) ? true : data[currentDateFormatted] === null ? null : goalCompleted ? -1 : false
							limitDate.setDate(limitDate.getDate() + 1)
						}
					} else {
						limitDate = new Date(lastMonthDate.setDate(lastMonthDate.getDate() + 1))
					}
				}
			}
		},
		getAccumulatedByWeeks (data) {
			data = data || this.$store.state.healthyHabits.chart.data
			let result = []
			let firstDate = data[0].date
			let lastDate = data[data.length - 1].date
			let pivotDate = this.getFirstWeekDate(firstDate)
			let lastWeekDay = this.getFirstWeekDate(lastDate)
			while (pivotDate <= lastWeekDay) {
				let weekFirstDate = new Date(pivotDate)
				pivotDate.setDate(pivotDate.getDate() + 7)
				let partialAccumulated = data.reduce((acc, item) => { if (item.date >= weekFirstDate && item.date < pivotDate) { return acc + item.value } else { return acc } }, 0)
				if (partialAccumulated > 0) {
					result.push({ x: weekFirstDate, y: partialAccumulated })
				}
			}
			return result
		},
		getAccumulatedByMonths (data) {
			data = data || this.$store.state.healthyHabits.chart.data
			let result = []
			let currentDate = data[0].date
			let lastDate = data[data.length - 1].date
			let currentMonthDate = currentDate.getMonth()
			let lastMonthDate = lastDate.getMonth()
			while (currentMonthDate <= lastMonthDate) {
				let partialAccumulated = data.reduce((acc, item) => { if (item.date.getMonth() === currentMonthDate) { return acc + item.value } else { return acc } }, 0)
				result.push({ x: new Date(currentDate.getFullYear(), currentMonthDate, 1), y: partialAccumulated })
				currentMonthDate += 1
			}
			return result
		},
		getAccumulatedByYears (data) {
			data = data || this.$store.state.healthyHabits.chart.data
			let result = []
			let currentDate = data[0].date
			let lastDate = data[data.length - 1].date
			let currentYearDate = currentDate.getFullYear()
			let lastYearDate = lastDate.getFullYear()
			while (currentYearDate <= lastYearDate) {
				let partialAccumulated = data.reduce((acc, item) => { if (item.date.getFullYear() === currentYearDate) { return acc + item.value } else { return acc } }, 0)
				result.push({ x: new Date(currentDate.getFullYear(), 1, 1), y: partialAccumulated })
				currentYearDate += 1
			}
			return result
		},
		getAccumulatedOfMonthByWeekDays (data, monthIndex) {
			let result = []
			for (let day = 6; day >= 0; day--) {
				let dayAccumulated = ''
				let accumulatedValuesByDay = data.filter(item => { let daySpanishFormat = item.date.getDay() === 0 ? 6 : item.date.getDay() - 1; if(daySpanishFormat === day) { dayAccumulated += `${this.getDateOnlySpanishFormat(item.date)}:<b>${item.value}</b><br>`; return true } else { return false } }).map(item => item.value).reduce((acc, value) => acc + value, 0)
				if (accumulatedValuesByDay > 0) {
					result.push({x: monthIndex, y: 6 - day, z: accumulatedValuesByDay, dayAccumulated: dayAccumulated})
				}
			}
			return result
		},
		getScoreData (item) {
			let data = item ? Object.keys(item).filter(key => this.checkValidDate(key) && item[key] !== null).map(key => ({date: new Date(key), value: !item.measurable ? item[key] === true ? 1 : 0 : item[key]})) : this.$store.state.healthyHabits.chart.data.filter(key => key.value !== null).map(key => ({...key}))
			if (data.length === 0) {
				return [{y: 0}]
			}
			data = this.fillDatesDefaultValue(data)
			data.sort((a, b) => b.date - a.date)
			let goalFrequencyId = item ? item.goalFrequencyId : this.$store.state.healthyHabits.chart.goalFrequencyId
			let goal = item ? item.goal : this.$store.state.healthyHabits.chart.goal
			let measurable = item ? item.measurable : this.$store.state.healthyHabits.chart.measurable
			let result = []
			let rollingSum = 0.0
			let dailyGoal = goalFrequencyId === DAILY_GOAL
			let frequencyInterval = dailyGoal ? 1 : goalFrequencyId === WEEKLY_GOAL ? 7 : 30
			let numerator = measurable ? 1 : dailyGoal ? frequencyInterval : goal
			let denominator = dailyGoal && !measurable ? goal : frequencyInterval
			let frequency = numerator / denominator
			if (!measurable && frequency < 1.0) {
				numerator *= 2
				denominator *= 2
			}
			let previousValue = 0
			for (let index in data) {
				let offset = data.length - index - 1
				let item = data[offset]
				if (measurable) {
					rollingSum += Math.max(0, item.value)
					if (offset + denominator < data.length) {
						rollingSum -= Math.max(0, data[offset + denominator].value)
					}
					if (item.value >= 0) {
						let percentageCompleted = goal > 0 ? Math.min(1.0, rollingSum / goal) : 1.0
							previousValue = this.computeScore(frequency, previousValue, percentageCompleted)
							result.push({ x: item.date.getTime(), y: Math.round(previousValue * 100) })
					}
				} else {
					if (item.value === 1) {
						rollingSum += 1.0
					}
					if (offset + denominator < data.length) {
						if (data[offset + denominator].value === 1) {
								rollingSum -= 1.0
						}
					}
					if (item.value !== null) {
						let percentageCompleted = Math.min(1.0, rollingSum / numerator)
						previousValue = this.computeScore(frequency, previousValue, percentageCompleted)
					}
					result.push({ x: item.date.getTime(), y: Math.round(previousValue * 100) })
				}
			}
			return result
		},
		computeScore (frequency, previousScore, checkmarkValue) {
			let multiplier = Math.pow(0.5, Math.sqrt(frequency) / 13.0)
			let score = previousScore * multiplier
			score += checkmarkValue * (1 - multiplier)
			return score
		},
		getScoreDataByWeeks () {
			let result = []
			let data = this.$store.state.healthyHabits.chart.score.dataCache
			let limitDate = this.getFirstWeekDate(data[0].x)
			let currentDate = new Date(data[data.length - 1].x)
			while (limitDate <= currentDate) {
				let limitWeek = new Date(currentDate).setDate(currentDate.getDate() - 7)
				let newValue = data.filter(item => new Date(item.x) <= currentDate && new Date(item.x) > limitWeek).sort((a, b) => new Date(b.x) - new Date(a.x))[0]
				if (newValue) {
					result.unshift(newValue)
				}
				currentDate.setDate(currentDate.getDate() - 7)
			}
			return result
		},
		getScoreDataByMonths () {
			let result = []
			let data = this.$store.state.healthyHabits.chart.score.dataCache
			let limitDate = new Date(new Date(new Date(data[0].x).getFullYear(), new Date(data[0].x).getMonth(), 1).setHours(0, 0, 0, 0))
			let currentDate = new Date(new Date(new Date(data[data.length - 1].x).getFullYear(), new Date(data[data.length - 1].x).getMonth() + 1, 0).setHours(0, 0, 0, 0))
			while (limitDate <= currentDate) {
				let limitWeek = new Date(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).setHours(0, 0, 0, 0))
				let newValue = data.filter(item => new Date(item.x) <= currentDate && new Date(item.x) > limitWeek).sort((a, b) => new Date(b.x) - new Date(a.x))[0]
				if (newValue) {
					result.unshift(newValue)
				}
				currentDate = new Date(new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).setHours(0, 0, 0, 0))
			}
			return result
		},
		getScoreDataByYears () {
			let result = []
			let data = this.$store.state.healthyHabits.chart.score.dataCache
			let limitDate = new Date(new Date(new Date(data[0].x).getFullYear(), 0, 1).setHours(0, 0, 0, 0))
			let currentDate = new Date(new Date(new Date(data[data.length - 1].x).getFullYear(), 11, 31).setHours(0, 0, 0, 0))
			while (limitDate <= currentDate) {
				let limitWeek = new Date(new Date(currentDate.getFullYear(), 0, 1).setHours(0, 0, 0, 0))
				let newValue = data.filter(item => new Date(item.x) <= currentDate && new Date(item.x) > limitWeek).sort((a, b) => new Date(b.x) - new Date(a.x))[0]
				if (newValue) {
					result.unshift(newValue)
				}
				currentDate = new Date(new Date(currentDate.getFullYear() - 1, 11, 31).setHours(0, 0, 0, 0))
			}
			return result
		},
		getFrequencyData (data) {
			data = data || this.$store.state.healthyHabits.chart.data
			let result = []
			let currentDate = new Date()
			let months = 12
			let currentMonthIndex = 11
			while (months > 0) {
				let dataFiltered = data.filter((item) => item.date.getMonth() === currentDate.getMonth() && item.date.getFullYear() === currentDate.getFullYear() && item.value > 0)
				if (dataFiltered.length > 0) {
					let accumulated = this.getAccumulatedOfMonthByWeekDays(dataFiltered, currentMonthIndex)
					result.push(...accumulated)
				} else {
					result.push({x: currentMonthIndex})
				}
				months--
				currentMonthIndex--
				currentDate.setMonth(currentDate.getMonth() - 1)
			}
			let maxValue = Math.max(...result.filter(item => item.z).map(item => item.z))
			result = result.map(item => { return {x: item.x, y: item.y, z: Math.round((item.z * 10) / maxValue), originalValue: item.z, dayAccumulated: item.dayAccumulated } })
			return result
		},
		getHeaderDatesInIntervalFormatted (startDate, endDate) {
			let result = []
			let currentDate = new Date(startDate)
			while (currentDate <= new Date(endDate)) {
				result.push({ key: this.getDateOnlyFormatted(currentDate) })
				currentDate.setDate(currentDate.getDate() + 1)
			}
			return this.$store.state.healthyHabits.setting.invertedCalendarDays ? result.reverse() : result
		},
		getCurrentDateUtc () {
			return new Date(new Date().setHours(0, 0, 0, 0))
		},
		getDateOnlyFormatted (date) {
			let currentDate = new Date(date)
			return `${currentDate.getFullYear()}-${('0' + (currentDate.getMonth() + 1)).slice(-2)}-${('0' + currentDate.getDate()).slice(-2)}T00:00:00`
		},
		getDateOnlySpanishFormat (date) {
			let currentDate = new Date(date)
			return `${('0' + currentDate.getDate()).slice(-2)}-${('0' + (currentDate.getMonth() + 1)).slice(-2)}-${currentDate.getFullYear()}`
		},
		getDatesOfWeekFromDate (date) {
			let result = []
			let inputDate = new Date(date)
			let inputDateDay = inputDate.getDay() == 0 ? 6 : inputDate.getDay() - 1
			let endDate = new Date(inputDate).setDate(inputDate.getDate() + (6 - inputDateDay))
			let currentDate = new Date(inputDate.setDate(inputDate.getDate() - inputDateDay))
			while (currentDate <= endDate) {
				result.push(this.getDateOnlyFormatted(new Date(currentDate)))
				currentDate.setDate(currentDate.getDate() + 1)
			}
			return result
		},
		getActualWeekDatesFormatted () {
			let result = []
			let endDate = new Date()
			let endDateDay = endDate.getDay() == 0 ? 6 : endDate.getDay() - 1
			let currentDate = new Date(new Date().setDate(new Date().getDate() - endDateDay))
			while (currentDate <= endDate) {
				result.push(this.getDateOnlyFormatted(new Date(currentDate)))
				currentDate.setDate(currentDate.getDate() + 1)
			}
			return result
		},
		getLastDateInWeek (date) {
			let currentDate = new Date(date)
			let currentDateDay = currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1
			currentDate.setDate(currentDate.getDate() + (6 - currentDateDay))
			return new Date(currentDate.setHours(0, 0, 0, 0))
		},
		getFirstWeekDate (date) {
			let currentDate = new Date(date)
			let currentDateDay = currentDate.getDay() === 0 ? 6 : currentDate.getDay() - 1
			currentDate.setDate(currentDate.getDate() - currentDateDay)
			return new Date(currentDate.setHours(0, 0, 0, 0))
		},
		checkValidDate (date) {
			let pattern = /^(?!0000)[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])T([01][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])Z?$/
			return pattern.test(date)
		},
		logOff () {
			window.$cookies.remove('api_token', null, getBaseDomain())      
			location.href = this.getHealthyHabitsPath()
		},
		logIn () {
			let _this = this
			this.$store.commit('session/setLoginCallback', function () {
				_this.$router.push(_this.getHealthyHabitsPath()).catch(() => {})
			})
			this.$router.push('/inicio-sesion')
		},
		getHealthyHabitsPath (path) {
			path = path ? path : ''
			if (location.href.includes('localdesarrollo')) {
				return `/habitossaludables${path ? '/' + path : ''}`
			} else {
				return `/${path}`
			}
		},
		goHealthyHabitsPath (path) {
			path = path ? path : ''
			if (location.href.includes('localdesarrollo')) {
				this.$router.push(`/habitossaludables${path ? '/' + path : ''}`).catch(() => {})
			} else {
				this.$router.push(`/${path}`).catch(() => {})
			}
		},
		getLastYearMonthNamesShort () {
			let result = []
			let currentDate = new Date()
			let currentMonth = new Date().getMonth()
			result.push(getMonthShortName(currentDate))
			let currentIndex = currentMonth === 0 ? 11 : (currentMonth - 1)
			while (currentIndex != currentMonth) {
				result.push(getMonthShortName(currentDate))
				currentIndex = currentIndex - 1 === -1 ? 11 : currentIndex - 1
			}
			return result.reverse()
		},
		daysInYear (year) {
			year = year || new Date().getFullYear()
			return ((year % 4 === 0 && year % 100 > 0) || year % 400 == 0) ? 366 : 365
		},
		measurableDataIsPartialCompleted (data, keyInput) {
			let dataKeys = Object.keys(data)
			let dates = []
			if (data.goalFrequencyId == DAILY_GOAL) {
				return data.goal <= data[keyInput]
			}
			if (data.goalFrequencyId == WEEKLY_GOAL) {
				dates = dataKeys.filter(key => this.checkValidDate(key) && this.getDatesOfWeekFromDate(keyInput).some(item => item === key))
				return dates.reduce((acc, date) => acc + data[date], 0) >= data.goal
			}
			if (data.goalFrequencyId == MONTHLY_GOAL) {
				let currentMonth = new Date(keyInput).getMonth() + 1
				dates = dataKeys.filter(key => this.checkValidDate(key) && currentMonth == parseFloat(key.split('-')[1]))
				return dates.reduce((acc, date) => acc + data[date], 0) >= data.goal
			}
		},
		getFrequencyName (goalFrequency){
			if (goalFrequency === DAILY_GOAL) {
				return this.$t('dailyShort')
		} else if (goalFrequency === WEEKLY_GOAL) {
				return this.$t('weeklyShort')
		} else if (goalFrequency === MONTHLY_GOAL) {
				return this.$t('monthlyShort')
		}
		},
		generateCalendar (dateModified) {
			let _this = this
			let result = '<table id="healthyHabitsCalendar">'
			let today = new Date()
			let rowColumns = 0
			let rowColumnsMax = 0
			let firstDate = new Date(new Date().setMonth(new Date().getMonth() - 4))
			let goalStreakUpdated = new Promise(() => {})
			if (firstDate.getDay() != 1) {
					let dayOfWeek = firstDate.getDay() == 0 ? 6 : firstDate.getDay() - 1
					firstDate = new Date(firstDate.setDate(firstDate.getDate() - dayOfWeek))
			}
			let pivotDate = new Date(firstDate)
			let lastMonth = null
			let item = this.$store.state.healthyHabits.chart.dataMapped
			if (dateModified) {
				if (!item.measurable) {
					let dataMapped = Object.keys(item).filter(key => this.checkValidDate(key)).map(date => new Date(date))
					this.fillPartialGoalCompleted(item, new Date(Math.min(...dataMapped)))
				}
				goalStreakUpdated = this.updateGoalStreak(true)
			} else if (!item.measurable) {
				this.fillPartialGoalCompletedCalendar(item, firstDate)
			}
			for (let i = 0; i <= 7; i++) {
					result += '<tr>'
					rowColumns = 0
					while (pivotDate <= today) {
							if (i === 0) {
									result += `<th class="cell-header">${pivotDate.getMonth() !== lastMonth ? getMonthShortName(pivotDate) : ''}</th>`
									lastMonth = pivotDate.getMonth()
									pivotDate.setDate(pivotDate.getDate() + 7)
							} else {
									result += `<td style="background-color: ${this.getCellBackgroundColor(item, pivotDate)}" id="calendar;${this.getDateOnlyFormatted(pivotDate)}" title="${this.getCellTitle(item, pivotDate)}" class="cell-style ${this.getCellOpacityClass(item, pivotDate)}">${pivotDate.getDate()}</td>`
									pivotDate.setDate(pivotDate.getDate() + 7)
									++rowColumns
							}
					}
					if (i > 0) {
							if (rowColumns > rowColumnsMax) {
									rowColumnsMax = rowColumns
							}
							result += `${rowColumnsMax > rowColumns ? `<td></td>` : ''}<td class="cell-week-day">${getWeekDayShortName(pivotDate)}</td></tr>`
							pivotDate = new Date(firstDate.setDate(firstDate.getDate() + 1))
					} else {
							result += '</tr>'
							pivotDate = new Date(firstDate)
					}
			}
			result += '</table>'
			document.getElementById('healthyHabitsCalendarContainer').innerHTML = result
			document.getElementById('healthyHabitsCalendar').removeEventListener('click')
			document.getElementById('healthyHabitsCalendar')
			.addEventListener('click', function (event) {
				if (_this.$store.state.healthyHabits.table.updatingItem) {
					return
				}
				let targetId = event.target.id
				if (targetId) {
						targetId = targetId.split(';')[1]
						let item = _this.$store.state.healthyHabits.chart.dataMapped
						_this.$store.state.healthyHabits.itemSelected.date = targetId
						_this.$store.state.healthyHabits.itemSelected.fromCalendar = true
						let itemValue = item[targetId]
						if (item.measurable) {
							_this.$store.state.healthyHabits.itemSelected.measureUnit = item.measureUnit
							_this.$store.state.healthyHabits.itemSelected.currentValue = itemValue
							_this.$store.state.healthyHabits.itemSelected.newValue = itemValue
							_this.$store.state.healthyHabits.showMeasurableInputModal = true
						} else {
							let itemValue = item[targetId]
							if (_this.$store.state.healthyHabits.setting.allowFreeDays) {
								_this.$store.state.healthyHabits.itemSelected.newValue = itemValue === true ? null : itemValue === null ? false : true
							} else {
								_this.$store.state.healthyHabits.itemSelected.newValue = !itemValue || itemValue === -1 ? true : false
							}
							_this.sendModifiedData()
						}
				}
			})
			if (dateModified) {
				goalStreakUpdated.then(() => {
					this.$store.state.healthyHabits.itemSelected.fromCalendar = false
					this.$store.state.healthyHabits.table.updatingItem = false
				})
			}
		},
		getCellBackgroundColor (item, date) {
			let itemValue = item[this.getDateOnlyFormatted(date)]
			return (!item.measurable && (itemValue === true || itemValue === null || itemValue === -1)) || (item.measurable && this.measurableDataIsPartialCompleted(item, this.getDateOnlyFormatted(date))) ? this.$store.state.healthyHabits.chart.color : GRAY_400_COLOUR
		},
		getCellOpacityClass (item, date) {
			let itemValue = item[this.getDateOnlyFormatted(date)]
			return item.measurable && itemValue >= item.goal ? '' : (!item.measurable && itemValue === null) || (item.measurable && itemValue > 0 && itemValue < item.goal) ? 'linear-gradient' : (!item.measurable && itemValue === -1) || (item.measurable && this.measurableDataIsPartialCompleted(item, this.getDateOnlyFormatted(date))) ? 'partial-fill-opacity' : ''
		},
		getCellTitle (item, date) {
			let itemValue = item[this.getDateOnlyFormatted(date)]
			return item.measurable && itemValue > 0 ? itemValue : ''
		},
		isRouteFromClinicHistory () {
			return this.$route.params.historyVarName || this.$route.params.historyGroupId
		}
	}
}


// WEBPACK FOOTER //
// ./src/mixins/healthyHabits.mixin.js